import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { AttorneyGeneralWizardDraft } from '../../../types/attorneyGeneralCaseRequest';
import type { RequiredErrors } from '../../../utils/requiredValidation';
import { WIZARD_BASE, isWizardCategorizationEditable, wizardReviewPath } from '../../../utils/wizardCategoryRoute';
import { StepBar, StepSep } from '../WizardComponents';
import { wizardStepBarLabel, wizardStepTitle } from '../wizardI18nHelpers';
import { AttorneyGeneralCaseCategorizationCard } from './AttorneyGeneralCaseCategorizationCard';
import { AttorneyGeneralWizardStep } from './AttorneyGeneralWizardStep';
import {
  ATTORNEY_GENERAL_WIZARD_STEPS,
  type AttorneyGeneralWizardStep as AttorneyGeneralWizardStepNum,
} from './attorneyGeneralConstants';
import {
  attorneyGeneralStepShowsLabel,
} from './attorneyGeneralHelpers';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

export function attorneyGeneralWizardPath(step: AttorneyGeneralWizardStepNum, caseRequestId?: string): string {
  const base = `${WIZARD_BASE}/step-${String(step)}`;
  const id = (caseRequestId ?? '').trim();
  return id ? `${base}/${id}` : base;
}

export function attorneyGeneralReviewPath(caseRequestId?: string): string {
  return wizardReviewPath(undefined, caseRequestId);
}

export function AttorneyGeneralWizardShell({
  currentStep,
  draft,
  setDraft,
  categorizationEditable = true,
  categorizationErrors,
  onClearCategorizationError,
  children,
}: {
  currentStep: AttorneyGeneralWizardStepNum;
  draft: AttorneyGeneralWizardDraft;
  setDraft?: React.Dispatch<React.SetStateAction<AttorneyGeneralWizardDraft>>;
  categorizationEditable?: boolean;
  categorizationErrors?: RequiredErrors;
  onClearCategorizationError?: (key: string) => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { roleCode } = useRole();
  const { setDraft: contextSetDraft } = useAttorneyGeneralWizardContext();
  const effectiveSetDraft = setDraft ?? contextSetDraft;
  const showWizard = Boolean(draft.fileCategoryId || draft.subFileCategoryId || draft.draftRequestId);

  return (
    <>
      <div className="bc">
        <Link to={tasksListPathForRole(roleCode)}>{t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        {t('wizard.createCaseRequest')}
      </div>

      <div className="pg-title">{t('wizard.createCaseRequest')}</div>
      <div className="pg-sub" style={{ marginBottom: 16 }}>
        {t('wizard.createSubtitle')}
      </div>

      <AttorneyGeneralCaseCategorizationCard
        draft={draft}
        setDraft={effectiveSetDraft}
        editable={isWizardCategorizationEditable(categorizationEditable, draft.draftRequestId)}
        errors={categorizationErrors}
        onClearError={onClearCategorizationError}
      />

      {showWizard ? (
        <>
          <StepBar className="ccr-step-bar--attorney-general">
            {ATTORNEY_GENERAL_WIZARD_STEPS.map((s, idx) => {
              const state = currentStep > s.n ? 'done' : currentStep === s.n ? 'active' : 'pending';
              const showLabel = attorneyGeneralStepShowsLabel(
                s.n,
                currentStep,
                ATTORNEY_GENERAL_WIZARD_STEPS.length,
              );
              return (
                <Fragment key={s.n}>
                  <div className="ccr-step-col">
                    <AttorneyGeneralWizardStep
                      state={state}
                      numberOrCheck={state === 'done' ? '✓' : String(s.n)}
                      stepLabel={wizardStepBarLabel(t, s.n)}
                      name={wizardStepTitle(t, 'attorneyGeneral', s.n)}
                      showLabel={showLabel}
                    />
                  </div>
                  {idx < ATTORNEY_GENERAL_WIZARD_STEPS.length - 1 ? <StepSep /> : null}
                </Fragment>
              );
            })}
          </StepBar>

          {children}
        </>
      ) : null}
    </>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
      {message}
    </div>
  );
}

export function inpClass(hasError: boolean): string {
  return `ccr-inp${hasError ? ' inv' : ''}`;
}
